import { useEffect, useState } from "react";
import axios from "axios";
import QRCode from "react-qr-code";
import { Check, Copy, Lock, LogOut, Mail, RefreshCw, Star, Trash2 } from "lucide-react";
import { Reveal } from "../components/Motion";
import { LogoMark } from "../components/Header";
import { useSiteSettings } from "../SiteSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "lv-admin-token";

const fmtErr = (detail) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
};

function SiteLinks({ token }) {
  const settings = useSiteSettings();
  const [form, setForm] = useState({
    booking_url: settings.booking_url,
    review_url: settings.review_url,
    whatsapp_number: settings.whatsapp_number,
  });
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!dirty) {
      setForm({
        booking_url: settings.booking_url,
        review_url: settings.review_url,
        whatsapp_number: settings.whatsapp_number,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.booking_url, settings.review_url, settings.whatsapp_number]);

  const set = (key) => (e) => {
    setDirty(true);
    setForm({ ...form, [key]: e.target.value });
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setSaved(false);
    try {
      const { data } = await axios.put(`${API}/admin/settings`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      settings.update(data);
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail));
    }
  };

  return (
    <div className="review-card-section" data-testid="site-links-section">
      <p className="eyebrow">Site links</p>
      <div className="rr-panel">
        <p className="rr-explainer">
          These power the buttons and widgets across the site. Change them anytime — they go live the moment you save.
        </p>
        {error && (
          <p className="form-error" data-testid="site-links-error">
            {error}
          </p>
        )}
        <form className="lv-form" onSubmit={save} data-testid="site-links-form">
          <label>
            Discovery-call booking link
            <input value={form.booking_url} onChange={set("booking_url")} placeholder="https://calendar.app.google/…" data-testid="booking-url-input" />
          </label>
          <label>
            Google review link
            <input value={form.review_url} onChange={set("review_url")} placeholder="https://share.google/…" data-testid="review-url-input" />
          </label>
          <label>
            WhatsApp number (digits, with country code)
            <input value={form.whatsapp_number} onChange={set("whatsapp_number")} placeholder="917796298181" data-testid="whatsapp-number-input" />
          </label>
          <button className="primary-button" type="submit" data-testid="site-links-save-button">
            {saved ? "Saved — live across the site" : "Save links"}
          </button>
        </form>
      </div>
    </div>
  );
}

function ReviewCard() {
  const { review_url } = useSiteSettings();
  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(review_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="review-card-section" data-testid="review-card-section">
      <p className="eyebrow">Client reviews</p>
      {review_url ? (
        <div className="review-card" data-testid="review-card">
          <div className="review-card-left">
            <img src="/logo-oasis-white.png" className="rc-logo" alt="Oasis Business Solutions" />
            <h3>Enjoyed the work?</h3>
            <p>Scan to review Oasis Business Solutions on Google — it takes a minute and means the world.</p>
            <a href={review_url} target="_blank" rel="noreferrer" className="text-link" data-testid="review-card-link">
              Open review link
            </a>
            <button className="icon-button" onClick={copyLink} data-testid="review-card-copy-button">
              {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy link"}
            </button>
          </div>
          <div className="review-qr" data-testid="review-card-qr">
            <QRCode value={review_url} size={150} bgColor="#121216" fgColor="#D4AF37" />
          </div>
        </div>
      ) : (
        <div className="review-card empty" data-testid="review-card-empty">
          <span className="lock-badge">
            <Star size={17} />
          </span>
          <div>
            <h3>Your review QR card appears here</h3>
            <p>
              Once your Google Business Profile is live: open it, choose <strong>“Ask for reviews”</strong>, copy the link
              and paste it in Site links above — this becomes a branded QR card you can send every client.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const RR_EMPTY = { client_name: "", client_email: "", project_name: "", send_after_days: "3" };

function ReviewRequests({ token }) {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(RR_EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/review-requests`, { headers });
      setItems(data.requests);
    } catch {
      setError("Could not load review requests.");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await axios.post(
        `${API}/admin/review-requests`,
        { ...form, send_after_days: parseInt(form.send_after_days, 10) },
        { headers }
      );
      setForm(RR_EMPTY);
      load();
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (id) => {
    try {
      await axios.delete(`${API}/admin/review-requests/${id}`, { headers });
      load();
    } catch {
      setError("Could not cancel that request.");
    }
  };

  return (
    <div className="review-card-section" data-testid="review-requests-section">
      <p className="eyebrow">Review ask automation</p>
      <div className="rr-panel">
        <p className="rr-explainer">
          When a project wraps, add the client here. We email them your Google review link after the delay you pick —
          you never have to remember to ask.
        </p>
        {error && (
          <p className="form-error" data-testid="rr-error">
            {error}
          </p>
        )}
        <form className="lv-form" onSubmit={submit} data-testid="rr-form">
          <div className="form-row">
            <label>
              Client name
              <input required value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="rr-name-input" />
            </label>
            <label>
              Client email
              <input required type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} data-testid="rr-email-input" />
            </label>
          </div>
          <div className="form-row">
            <label>
              Project
              <input required value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} data-testid="rr-project-input" />
            </label>
            <label>
              Send the review ask
              <select value={form.send_after_days} onChange={(e) => setForm({ ...form, send_after_days: e.target.value })} data-testid="rr-delay-select">
                <option value="1">1 day after</option>
                <option value="3">3 days after</option>
                <option value="7">7 days after</option>
              </select>
            </label>
          </div>
          <button className="primary-button" type="submit" disabled={busy} data-testid="rr-submit-button">
            {busy ? "Scheduling…" : "Schedule review ask"}
          </button>
        </form>
      </div>
      {items && items.length > 0 && (
        <div className="rr-list" data-testid="rr-list">
          {items.map((req, i) => (
            <div className="rr-row" key={req.id} data-testid={`rr-row-${i + 1}`}>
              <div className="rr-main">
                <strong>{req.client_name}</strong>
                <span>
                  {req.project_name} · {req.client_email}
                </span>
              </div>
              <div className="rr-meta">
                <span className="brief-date">
                  {req.status === "sent"
                    ? `Sent ${new Date(req.sent_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`
                    : `Sends ${new Date(req.send_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`}
                </span>
                <span className={`tag ${req.status === "sent" ? "sent" : "gold"}`}>{req.status}</span>
                {req.status === "scheduled" && (
                  <button className="icon-button" onClick={() => cancel(req.id)} data-testid={`rr-cancel-${i + 1}`} aria-label="Cancel review request">
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [inquiries, setInquiries] = useState(null);

  const load = async (tok) => {
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/inquiries`, {
        headers: { Authorization: `Bearer ${tok || token}` },
      });
      setInquiries(data.inquiries);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        setInquiries(null);
      } else {
        setError("Could not load briefs right now.");
      }
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setInquiries(null);
  };

  if (!token) {
    return (
      <section className="admin-wrap" data-testid="admin-login-page">
        <Reveal className="admin-login">
          <span className="lock-badge">
            <Lock size={18} />
          </span>
          <p className="eyebrow">Studio only</p>
          <h1>Studio access.</h1>
          {error && (
            <p className="form-error" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <form className="lv-form" onSubmit={login} data-testid="admin-login-form">
            <label>
              Email
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" />
            </label>
            <label>
              Password
              <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" />
            </label>
            <button className="primary-button" type="submit" disabled={loading} data-testid="admin-login-submit-button">
              {loading ? "Checking…" : "Enter the studio"}
            </button>
          </form>
        </Reveal>
      </section>
    );
  }

  return (
    <section className="admin-wrap" data-testid="admin-dashboard">
      <div className="admin-head">
        <h1>
          Briefs <span>({inquiries ? inquiries.length : "…"})</span>
        </h1>
        <div className="admin-actions">
          <button className="icon-button" onClick={() => load()} data-testid="admin-refresh-button">
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="icon-button" onClick={logout} data-testid="admin-logout-button">
            <LogOut size={14} /> Log out
          </button>
        </div>
      </div>
      {error && (
        <p className="form-error" data-testid="admin-load-error">
          {error}
        </p>
      )}
      {inquiries && inquiries.length === 0 && (
        <p className="admin-empty" data-testid="admin-empty-state">
          No briefs yet. When someone writes in through the site, they land here — and in your inbox.
        </p>
      )}
      <div className="brief-grid">
        {inquiries?.map((inq, i) => (
          <Reveal key={inq.id || i} delay={Math.min(i, 6) * 0.05} className="brief-card">
            <div data-testid={`inquiry-card-${i + 1}`}>
              <div className="brief-top">
                <h3>{inq.name}</h3>
                <span className="brief-date">{inq.created_at ? new Date(inq.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</span>
              </div>
              <a className="brief-contact" href={`mailto:${inq.email}`} data-testid={`inquiry-email-${i + 1}`}>
                <Mail size={12} style={{ verticalAlign: "-1px", marginRight: 6 }} />
                {inq.email}
              </a>
              <div className="brief-tags">
                <span className="tag gold">{inq.project_type}</span>
                {inq.budget && <span className="tag">{inq.budget}</span>}
                {inq.company && <span className="tag">{inq.company}</span>}
              </div>
              <p className="brief-msg">{inq.message}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <SiteLinks token={token} />
      <ReviewRequests token={token} />
      <ReviewCard />
    </section>
  );
}
